import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Card, Tag, Space, Modal, Form, Statistic, Row, Col, Descriptions, Tooltip } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import {
  loadAccountApprovals,
  approveAccount,
  rejectAccount,
  loadApprovalStatistics
} from 'store/slices/SupportSlice';
import { format } from 'date-fns';
import useUrlFilters from 'hooks/useUrlFilters';

const { Search } = Input;
const { TextArea } = Input;

const URL_FILTERS = {
  search: { type: 'string' },
  status: { type: 'string', default: 'pending' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const SupportApprovals = () => {
  const dispatch = useDispatch();

  const approvals = useSelector(state => state.support.approvals);
  const loading = useSelector(state => state.support.approvalsLoading);
  const pagination = useSelector(state => state.support.approvalsPagination);
  const statistics = useSelector(state => state.support.approvalStatistics);

  // Search, status and page live in the URL.
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const searchValue = filters.search || '';
  const [searchLive, setSearchLive] = useState(searchValue);
  useEffect(() => setSearchLive(searchValue), [searchValue]);
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilters({ status: value, page: 1 });
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [rejectForm] = Form.useForm();

  useEffect(() => {
    dispatch(loadApprovalStatistics());
  }, [dispatch]);

  useEffect(() => {
    loadData(currentPage);
  }, [statusFilter, searchValue, currentPage, pageSize]);

  const loadData = (page) => {
    dispatch(loadAccountApprovals({ page, searchValue, status: statusFilter, page_size: pageSize }));
  };

  const handleSearch = (value) => {
    setFilters({ search: value, page: 1 });
  };

  const handleApprove = async (accountId) => {
    Modal.confirm({
      title: 'Approve Account?',
      content: 'The customer will receive an email notification and can start using the support portal.',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          await dispatch(approveAccount({
            accountId,
            frontend_url: process.env.REACT_APP_SUPPORT_PORTAL_URL || 'http://localhost:3001'
          })).unwrap();
          loadData(currentPage);
          dispatch(loadApprovalStatistics());
        } catch (error) {
          console.error('Failed to approve:', error);
        }
      }
    });
  };

  const handleRejectClick = (account) => {
    setSelectedAccount(account);
    setRejectModalVisible(true);
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      await dispatch(rejectAccount({
        accountId: selectedAccount.id,
        reason: values.reason
      })).unwrap();

      setRejectModalVisible(false);
      rejectForm.resetFields();
      loadData(currentPage);
      dispatch(loadApprovalStatistics());
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  const showDetails = (account) => {
    setSelectedAccount(account);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Contact Person',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.first_name} {record.last_name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.email}</div>
        </div>
      )
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-'
    },
    {
      title: 'Status',
      dataIndex: 'approval_status_display',
      key: 'status',
      width: 120,
      render: (text, record) => {
        const color =
          record.approval_status === 'APPROVED' ? 'green' :
          record.approval_status === 'REJECTED' ? 'red' :
          'orange';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: 'Email Verified',
      dataIndex: 'support_email_verified',
      key: 'verified',
      width: 120,
      render: (verified) => verified ? (
        <Tag color="green">Yes</Tag>
      ) : (
        <Tag color="red">No</Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => format(new Date(date), 'MMM dd, yyyy')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
          >
            Details
          </Button>
          {record.approval_status === 'PENDING' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(record.id)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => handleRejectClick(record)}
              >
                Reject
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Statistics */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pending Approvals"
                value={statistics.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Approved"
                value={statistics.approved}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Rejected"
                value={statistics.rejected}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total"
                value={statistics.total}
                prefix={<UserAddOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Search
            placeholder="Search accounts..."
            value={searchLive}
            onChange={(e) => setSearchLive(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
          />

          <Button.Group>
            <Button
              type={statusFilter === 'pending' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('pending')}
            >
              Pending
            </Button>
            <Button
              type={statusFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('all')}
            >
              All
            </Button>
            <Button
              type={statusFilter === 'approved' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('approved')}
            >
              Approved
            </Button>
            <Button
              type={statusFilter === 'rejected' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('rejected')}
            >
              Rejected
            </Button>
          </Button.Group>

          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadData(currentPage)}
              aria-label="Refresh"
            />
          </Tooltip>
        </Space>
      </Card>

      {/* Table */}
      <Card title="Account Approvals">
        <Table
          columns={columns}
          dataSource={approvals}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            total: pagination.totalCount,
            pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} accounts`,
            // A new page size starts again from page 1
            onChange: (page, size) => setFilters({ page: size !== pageSize ? 1 : page, pageSize: size })
          }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Account Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={selectedAccount?.approval_status === 'PENDING' ? [
          <Button key="reject" danger onClick={() => {
            setDetailModalVisible(false);
            handleRejectClick(selectedAccount);
          }}>
            Reject
          </Button>,
          <Button key="approve" type="primary" onClick={() => {
            setDetailModalVisible(false);
            handleApprove(selectedAccount.id);
          }}>
            Approve
          </Button>
        ] : [
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedAccount && (
          <>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Company Name" span={2}>
                <strong>{selectedAccount.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="First Name">
                {selectedAccount.first_name}
              </Descriptions.Item>
              <Descriptions.Item label="Last Name">
                {selectedAccount.last_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {selectedAccount.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedAccount.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Account Type">
                {selectedAccount.account_type}
              </Descriptions.Item>
              <Descriptions.Item label="Account Tier">
                <Tag color="blue">{selectedAccount.account_tier}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Email Verified">
                {selectedAccount.support_email_verified ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Approval Status">
                <Tag color={
                  selectedAccount.approval_status === 'APPROVED' ? 'green' :
                  selectedAccount.approval_status === 'REJECTED' ? 'red' :
                  'orange'
                }>
                  {selectedAccount.approval_status_display}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {format(new Date(selectedAccount.created_at), 'MMM dd, yyyy hh:mm a')}
              </Descriptions.Item>
              {selectedAccount.approved_by && (
                <>
                  <Descriptions.Item label="Approved By">
                    {selectedAccount.approved_by.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approved At">
                    {format(new Date(selectedAccount.approved_at), 'MMM dd, yyyy hh:mm a')}
                  </Descriptions.Item>
                </>
              )}
              {selectedAccount.company_details && (
                <Descriptions.Item label="Company Details" span={2}>
                  {selectedAccount.company_details}
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Account"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Explain why this account is being rejected..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportApprovals;
